import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApiClient } from '../lib/apiClient';
import { Wordmark } from '../components/Logo';
import NewAppointmentModal from '../components/NewAppointmentModal';
import { addDays, endOfDayUTC, startOfDayUTC, todayLocal, toLocalDayLong, toLocalTime } from '../lib/datetime';
import { formatARS } from '../lib/format';
import { errorCopy } from '../lib/errors';

interface Appointment {
  id: string;
  client_id: string;
  barber_id: string;
  service_id: string;
  starts_at: string;
  ends_at: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'no_show' | 'cancelled';
  price_cents: number;
  client_name: string;
  client_phone: string;
  service_name: string;
  duration_min: number;
  barber_name: string;
}

interface Service {
  id: string;
  name: string;
  price_cents: number;
  duration_min: number;
  active: number;
}

interface Barber {
  id: string;
  name: string;
  role: 'owner' | 'barber';
  active: number;
}

const STATUS_LABEL: Record<Appointment['status'], string> = {
  scheduled: 'Agendado',
  confirmed: 'Confirmado',
  completed: 'Cortado',
  no_show: 'Te clavó',
  cancelled: 'Cancelado',
};

const STATUS_TINT: Record<Appointment['status'], string> = {
  scheduled: 'bg-ink/5 border-ink/15 text-ink',
  confirmed: 'bg-forest/10 border-forest/30 text-forest',
  completed: 'bg-olive/10 border-olive/30 text-olive',
  no_show: 'bg-danger/10 border-danger/30 text-danger',
  cancelled: 'bg-ash/15 border-ash/30 text-ash line-through',
};

export default function AgendaPage() {
  const { user, salon, logout } = useAuth();
  const apiClient = useApiClient();

  const [date, setDate] = useState(() => todayLocal());
  const [appointments, setAppointments] = useState<Appointment[] | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [selectedBarber, setSelectedBarber] = useState<string | 'all'>('all');
  const [showModal, setShowModal] = useState(false);
  const [modalTime, setModalTime] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadDay = useCallback(async (d: string) => {
    setAppointments(null);
    const res = await apiClient<{ appointments: Appointment[] }>(
      `/api/appointments?from=${encodeURIComponent(startOfDayUTC(d))}&to=${encodeURIComponent(endOfDayUTC(d))}`,
    );
    if (res.ok) setAppointments(res.data.appointments);
    else setAppointments([]);
  }, [apiClient]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, u] = await Promise.all([
        apiClient<{ services: Service[] }>('/api/services'),
        apiClient<{ users: Barber[] }>('/api/users'),
      ]);
      if (cancelled) return;
      if (s.ok) setServices(s.data.services);
      if (u.ok) setBarbers(u.data.users);
    })();
    return () => { cancelled = true; };
  }, [apiClient]);

  useEffect(() => {
    loadDay(date);
  }, [date, loadDay]);

  const visible = useMemo(() => {
    if (!appointments) return null;
    return appointments.filter((a) => selectedBarber === 'all' || a.barber_id === selectedBarber);
  }, [appointments, selectedBarber]);

  async function changeStatus(appt: Appointment, status: Appointment['status']) {
    setActionError(null);
    const res = await apiClient(`/api/appointments/${appt.id}`, { method: 'PATCH', body: { status } });
    if (!res.ok) { setActionError(errorCopy(res.error)); return; }
    loadDay(date);
  }

  const dayDate = new Date(`${date}T03:00:00.000Z`);

  return (
    <main className="min-h-screen">
      <nav className="border-b border-ink/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Wordmark className="text-ink" />
          <Link to="/app" className="text-sm text-ink/60 hover:text-ink">← Inicio</Link>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-ink/70 hidden sm:inline">
            {salon?.name} · {user?.name?.split(' ')[0]}
          </span>
          <button onClick={logout} className="text-ink/60 hover:text-danger transition">Salir</button>
        </div>
      </nav>

      <section className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setDate(addDays(date, -1))} className="px-3 py-2 rounded-lg border border-ink/15 hover:bg-ink/5">←</button>
            <button onClick={() => setDate(todayLocal())} className="px-3 py-2 rounded-lg border border-ink/15 hover:bg-ink/5 text-sm">Hoy</button>
            <button onClick={() => setDate(addDays(date, 1))} className="px-3 py-2 rounded-lg border border-ink/15 hover:bg-ink/5">→</button>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tighter ml-3">
              {toLocalDayLong(dayDate)}
            </h1>
          </div>
          <button onClick={() => { setModalTime(undefined); setShowModal(true); }} className="btn-primary">+ Nuevo turno</button>
        </div>

        {barbers.filter((b) => b.active).length > 1 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Chip active={selectedBarber === 'all'} onClick={() => setSelectedBarber('all')}>Todos</Chip>
            {barbers.filter((b) => b.active).map((b) => (
              <Chip key={b.id} active={selectedBarber === b.id} onClick={() => setSelectedBarber(b.id)}>{b.name}</Chip>
            ))}
          </div>
        )}

        {actionError && (
          <div className="mb-4 text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-4 py-3">{actionError}</div>
        )}

        {visible === null ? (
          <p className="text-ash py-20 text-center">Bancá un toque…</p>
        ) : visible.length === 0 ? (
          <EmptyDay onAdd={() => { setModalTime(undefined); setShowModal(true); }} />
        ) : (
          <ul className="space-y-2">
            {visible.map((a) => (
              <AppointmentRow key={a.id} appt={a} onStatusChange={(s) => changeStatus(a, s)} />
            ))}
          </ul>
        )}
      </section>

      {showModal && (
        <NewAppointmentModal
          date={date}
          time={modalTime}
          barbers={barbers}
          services={services}
          defaultBarberId={selectedBarber === 'all' ? undefined : selectedBarber}
          onClose={() => setShowModal(false)}
          onCreated={() => loadDay(date)}
        />
      )}
    </main>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-full border text-sm transition ${active ? 'bg-ink text-bone border-ink' : 'border-ink/15 hover:bg-ink/5'}`}>
      {children}
    </button>
  );
}

function AppointmentRow({ appt, onStatusChange }: { appt: Appointment; onStatusChange: (s: Appointment['status']) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <li className={`flex items-center gap-4 p-4 rounded-xl border bg-white relative ${STATUS_TINT[appt.status]}`}>
      <div className="num text-lg font-medium w-20 shrink-0">
        {toLocalTime(appt.starts_at)}
        <div className="text-xs text-ash">{appt.duration_min} min</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{appt.client_name}</div>
        <div className="text-sm text-ink/70 truncate">
          {appt.service_name} · {appt.barber_name}
        </div>
      </div>
      <div className="num text-sm font-medium hidden sm:block">{formatARS(appt.price_cents)}</div>
      <div className="text-xs uppercase tracking-widest font-medium hidden md:block">{STATUS_LABEL[appt.status]}</div>

      <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded hover:bg-ink/5" aria-label="Acciones">⋯</button>
      {menuOpen && (
        <div className="absolute right-2 top-full mt-1 z-10 bg-white border border-ink/15 rounded-lg shadow-lg py-1 min-w-[160px]" onMouseLeave={() => setMenuOpen(false)}>
          {appt.status === 'scheduled' && (
            <MenuItem onClick={() => { setMenuOpen(false); onStatusChange('confirmed'); }}>Confirmar</MenuItem>
          )}
          {(appt.status === 'scheduled' || appt.status === 'confirmed') && (
            <>
              <MenuItem onClick={() => { setMenuOpen(false); onStatusChange('completed'); }}>Marcar como cortado</MenuItem>
              <MenuItem onClick={() => { setMenuOpen(false); onStatusChange('no_show'); }}>Te clavó</MenuItem>
              <MenuItem onClick={() => { setMenuOpen(false); onStatusChange('cancelled'); }} danger>Cancelar turno</MenuItem>
            </>
          )}
          {(appt.status === 'completed' || appt.status === 'no_show' || appt.status === 'cancelled') && (
            <MenuItem onClick={() => { setMenuOpen(false); onStatusChange('scheduled'); }}>Reabrir</MenuItem>
          )}
        </div>
      )}
    </li>
  );
}

function MenuItem({ onClick, children, danger }: { onClick: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button onClick={onClick} className={`w-full text-left px-3 py-2 hover:bg-ink/5 ${danger ? 'text-danger' : ''}`}>{children}</button>
  );
}

function EmptyDay({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="text-center py-20 border border-dashed border-ink/15 rounded-2xl">
      <p className="text-ink/70 mb-4">No tenés turnos cargados para este día.</p>
      <button onClick={onAdd} className="btn-primary">Cargar el primero</button>
    </div>
  );
}
